/**
 * My Attendance — V6-04 §4.
 *
 * A month coloured by verdict, a day drill-down that shows the sessions the
 * verdict came from, and a correction form reachable from any of it. The
 * drill-down matters more than the grid: "why does it say half day" is the
 * question this page exists to answer, and it can only be answered by showing
 * the clock times it was computed from.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { CalendarDays, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { AttendanceDayStatus, AttendanceDayView } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { AttendanceTabs } from './AttendanceTabs';
import { currentMonth } from './attendanceMonth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { elapsedLabel } from './DayChip';
import {
  useAttendanceDay,
  useAttendanceMonth,
  useCorrections,
  useRequestCorrection,
} from './api';

/**
 * Status colours, stated once.
 *
 * `non_working` and `holiday` are deliberately quiet rather than red: a grid
 * that shouts about Sundays trains people to ignore the colour that matters.
 */
export const STATUS_STYLE: Record<AttendanceDayStatus, { label: string; className: string }> = {
  present: { label: 'Present', className: 'bg-[var(--accent-soft)] text-[var(--accent)]' },
  half_day: { label: 'Half day', className: 'bg-[color-mix(in_srgb,var(--warn)_18%,transparent)] text-[var(--warn)]' },
  absent: { label: 'Absent', className: 'bg-[color-mix(in_srgb,var(--danger)_18%,transparent)] text-[var(--danger)]' },
  leave: { label: 'Leave', className: 'bg-[var(--surface-2)] text-[var(--text)]' },
  holiday: { label: 'Holiday', className: 'bg-[var(--surface-2)] vt-muted' },
  non_working: { label: 'Non-working', className: 'vt-muted' },
};

export function monthLabel(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(Date.UTC(year, monthNumber - 1, 1)).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function shiftMonth(month: string, by: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, monthNumber - 1 + by, 1));
  return shifted.toISOString().slice(0, 7);
}

function timeOf(iso?: string | null): string {
  return iso
    ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : '—';
}

/** `YYYY-MM-DDTHH:mm` for a datetime-local input, in the browser's zone. */
function toLocalInput(date: string, hhmm: string): string {
  return `${date}T${hhmm}`;
}

function CorrectionForm({
  date,
  sessionId,
  onDone,
}: {
  date: string;
  sessionId?: string;
  onDone: () => void;
}) {
  const request = useRequestCorrection();
  const [start, setStart] = useState(() => toLocalInput(date, '10:00'));
  const [end, setEnd] = useState(() => toLocalInput(date, '19:00'));
  const [reason, setReason] = useState('');

  async function submit(): Promise<void> {
    try {
      await request.mutateAsync({
        date,
        sessionId,
        // `datetime-local` has no zone, so the browser's is used — which is the
        // one the person is typing in.
        proposedStartAt: new Date(start).toISOString(),
        proposedEndAt: new Date(end).toISOString(),
        reason: reason.trim(),
      });
      toast.success('Correction requested — an approver will review it');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not request the correction');
    }
  }

  return (
    <div className="vt-surface flex flex-col gap-2 p-3" data-testid="correction-form">
      <h3 className="text-[13px] font-medium">Request a correction for {date}</h3>
      <div className="flex flex-wrap gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="correction-start">Started at</Label>
          <Input
            id="correction-start"
            type="datetime-local"
            data-testid="correction-start"
            value={start}
            onChange={(event) => setStart(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="correction-end">Ended at</Label>
          <Input
            id="correction-end"
            type="datetime-local"
            data-testid="correction-end"
            value={end}
            onChange={(event) => setEnd(event.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="correction-reason">Why</Label>
        <Input
          id="correction-reason"
          data-testid="correction-reason"
          placeholder="Forgot to end — left at 19:00"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      <div className="flex gap-1.5">
        <Button
          variant="primary"
          size="sm"
          data-testid="correction-submit"
          disabled={!reason.trim() || request.isPending}
          onClick={() => void submit()}
        >
          {request.isPending ? <Loader2 className="animate-spin" /> : null}
          Request correction
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function DayDetail({ date, onCorrect }: { date: string; onCorrect: () => void }) {
  const { data: day, isLoading } = useAttendanceDay(date);

  if (isLoading) return <Skeleton className="h-24" />;
  if (!day) return null;

  return (
    <div className="vt-surface flex flex-col gap-2 p-3" data-testid="day-detail" data-date={date}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[13px] font-medium">{date}</h3>
        <Badge tone={day.status === 'absent' ? 'danger' : 'accent'} data-testid="day-detail-status">
          {STATUS_STYLE[day.status].label}
        </Badge>
        <span className="vt-mono text-[12px] vt-muted" data-testid="day-detail-total">
          {elapsedLabel(day.totalMinutes)}
        </span>
        {day.flags.map((flag) => (
          <Badge key={flag} tone="warn" data-testid="day-flag">
            {flag.replace('_', ' ')}
          </Badge>
        ))}
      </div>

      <table className="w-full text-[12px]">
        <thead>
          <tr className="vt-muted">
            <th className="text-left font-normal">In</th>
            <th className="text-left font-normal">Out</th>
            <th className="text-left font-normal">Length</th>
            <th className="text-left font-normal">Ended by</th>
          </tr>
        </thead>
        <tbody>
          {(day.sessions ?? []).map((session) => (
            <tr key={session._id} data-testid="day-session">
              <td className="vt-mono">{timeOf(session.startAt)}</td>
              <td className="vt-mono">{timeOf(session.endAt)}</td>
              <td className="vt-mono">{elapsedLabel(session.minutes)}</td>
              <td className="vt-muted">{session.endSource ?? 'running'}</td>
            </tr>
          ))}
          {(day.sessions ?? []).length === 0 && (
            <tr>
              <td colSpan={4} className="py-2 vt-muted">
                No sessions recorded.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Button variant="secondary" size="sm" className="self-start" data-testid="request-correction" onClick={onCorrect}>
        Request correction
      </Button>
    </div>
  );
}

export function MyAttendancePage() {
  const [params, setParams] = useSearchParams();
  const [month, setMonth] = useState(() => currentMonth());
  const [selected, setSelected] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<string | null>(null);

  const { data, isLoading } = useAttendanceMonth(month);
  const { data: mine = [] } = useCorrections({ mine: true });

  // The auto-close banner deep-links straight to the form for the day it is
  // complaining about (§4, "one click to the correction form").
  const requested = params.get('correct');
  useEffect(() => {
    if (!requested) return;
    setMonth(requested.slice(0, 7));
    setSelected(requested);
    setCorrecting(requested);
    params.delete('correct');
    setParams(params, { replace: true });
  }, [requested]);

  const byDate = useMemo(
    () => new Map((data?.days ?? []).map((day: AttendanceDayView) => [day.date, day])),
    [data],
  );

  const totals = data?.totals;

  return (
    <>
      {/* V9-04 §2 — the Team view is one click away, from either page. */}
      <AttendanceTabs />

      <PageHeader
        title="My Attendance"
        subtitle={monthLabel(month)}
        actions={
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" data-testid="month-prev" onClick={() => setMonth(shiftMonth(month, -1))}>
              ‹
            </Button>
            <span className="vt-mono text-[12px]" data-testid="month-label">
              {month}
            </span>
            <Button variant="ghost" size="sm" data-testid="month-next" onClick={() => setMonth(shiftMonth(month, 1))}>
              ›
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-4 p-4">
        {totals && (
          <section className="flex flex-wrap gap-3" data-testid="month-totals">
            <Totals label="Present" value={totals.present} />
            <Totals label="Half days" value={totals.halfDay} />
            <Totals label="Absent" value={totals.absent} />
            <Totals label="Leave" value={totals.leave} />
            <Totals label="Lates" value={totals.lates} />
            <Totals label="Hours" value={(totals.totalMinutes / 60).toFixed(1)} />
          </section>
        )}

        {isLoading ? (
          <Skeleton className="h-64" />
        ) : (data?.days ?? []).length === 0 ? (
          <EmptyState
            icon={<CalendarDays className="size-6" />}
            title="Nothing recorded this month"
            description="Start your workday from the chip in the top bar and it will appear here."
          />
        ) : (
          <section className="vt-surface p-2" data-testid="attendance-calendar">
            <div className="grid grid-cols-7 gap-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((name) => (
                <div key={name} className="px-1 pb-1 text-[11px] vt-muted">
                  {name}
                </div>
              ))}
              {leadingBlanks(data!.days[0].date).map((key) => (
                <div key={key} aria-hidden />
              ))}
              {data!.days.map((day) => (
                <button
                  key={day.date}
                  type="button"
                  data-testid="attendance-day"
                  data-date={day.date}
                  data-status={day.status}
                  onClick={() => {
                    setSelected(day.date);
                    setCorrecting(null);
                  }}
                  className={cn(
                    'flex min-h-[52px] flex-col items-start rounded-[6px] p-1 text-left text-[11px] transition-colors',
                    STATUS_STYLE[day.status].className,
                    selected === day.date && 'outline outline-2 outline-[var(--accent)]',
                  )}
                >
                  <span className="vt-mono font-medium">{day.date.slice(-2)}</span>
                  {day.totalMinutes > 0 && (
                    <span className="vt-mono opacity-80">{elapsedLabel(day.totalMinutes)}</span>
                  )}
                  {day.flags.includes('late') && <span className="opacity-80">late</span>}
                </button>
              ))}
            </div>
          </section>
        )}

        {selected && !correcting && (
          <DayDetail date={selected} onCorrect={() => setCorrecting(selected)} />
        )}
        {correcting && (
          <CorrectionForm
            date={correcting}
            sessionId={byDate.get(correcting)?.sessions?.[0]?._id}
            onDone={() => setCorrecting(null)}
          />
        )}

        {mine.length > 0 && (
          <section className="vt-surface p-3" data-testid="my-corrections">
            <h3 className="mb-2 text-[13px] font-medium">My correction requests</h3>
            <ul className="flex flex-col gap-1 text-[12px]">
              {mine.map((correction) => (
                <li
                  key={correction._id}
                  className="flex items-center gap-2"
                  data-testid="my-correction-row"
                  data-status={correction.status}
                >
                  <span className="vt-mono">{correction.date}</span>
                  <span className="min-w-0 flex-1 truncate vt-muted">{correction.reason}</span>
                  <Badge
                    tone={
                      correction.status === 'approved'
                        ? 'accent'
                        : correction.status === 'rejected'
                          ? 'danger'
                          : 'warn'
                    }
                  >
                    {correction.status}
                  </Badge>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}

/** Empty cells so the first of the month lands under the right weekday. */
function leadingBlanks(firstDate: string): string[] {
  const [year, month, day] = firstDate.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return Array.from({ length: weekday }, (_, index) => `blank-${index}`);
}

function Totals({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="vt-surface min-w-[92px] px-3 py-2" data-testid="total" data-label={label}>
      <div className="text-[11px] vt-muted">{label}</div>
      <div className="vt-mono text-[16px]">{value}</div>
    </div>
  );
}
